import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Alert, LinearProgress, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Paper
} from '@mui/material';
import { CloudUpload, Download, Close, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import api from '../../utils/api';

function BulkImportDialog({ open, onClose, onSuccess, showSnackbar }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  const handleDownloadSample = () => {
    const csvContent = 'name,phone,email\nJohn Doe,9876543210,john@example.com\nJane Smith,9123456789,jane@example.com\nRahul Kumar,8765432190,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        showSnackbar('Please select a CSV file', 'error');
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/librarian/students/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(response.data.data);
      if (response.data.data.created > 0) {
        showSnackbar(`${response.data.data.created} students imported successfully`, 'success');
        onSuccess();
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to import students', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="700">Bulk Import Students</Typography>
          <IconButton size="small" onClick={handleClose}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload a CSV file with columns: <strong>name</strong>, <strong>phone</strong>, <strong>email</strong>.
          Name is required. Phone and email are optional. Default password for all imported students will be <strong>Welcome@123</strong>.
        </Alert>

        {/* Download Sample */}
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadSample}
          fullWidth
          sx={{ mb: 2, borderRadius: 2, py: 1.2 }}
        >
          Download Sample CSV
        </Button>

        {/* Upload Area */}
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: selectedFile ? 'success.main' : 'divider',
            borderRadius: 3,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: selectedFile ? 'success.50' : 'action.hover',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.selected',
            },
            mb: 2,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 40, color: selectedFile ? 'success.main' : 'text.secondary', mb: 1 }} />
          {selectedFile ? (
            <Box>
              <Typography variant="body1" fontWeight="600" color="success.main">
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024).toFixed(1)} KB — Click to change
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" fontWeight="600">
                Click to select CSV file
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Maximum file size: 2MB
              </Typography>
            </Box>
          )}
        </Box>

        {/* Upload Progress */}
        {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {/* Results */}
        {result && (
          <Box>
            <Box display="flex" gap={1} mb={2}>
              <Chip
                icon={<CheckCircle />}
                label={`${result.created} Created`}
                color="success"
                variant="filled"
              />
              {result.skipped > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${result.skipped} Skipped`}
                  color="warning"
                  variant="filled"
                />
              )}
            </Box>

            {result.errors.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Row</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((err, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell>{err.name || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="error">{err.reason}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startIcon={<CloudUpload />}
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
            }}
          >
            {uploading ? 'Importing...' : 'Import Students'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkImportDialog;
